import { useEffect, useState } from 'react';
import { Archive, ArchiveRestore, Lock, LogOut } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmModal,
  CopyButton,
  InlineEdit,
  Textarea,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useSpaceContext } from '@/contexts/SpaceContext';
import { useAuth } from '@/store/auth-context';
import { useNavigate } from 'react-router-dom';
import { useUpdateSpace, useUnarchiveSpace } from '@/hooks/useSpaceManagement';
import { useRemoveMember } from '@/hooks/useMembers';
import { useSpaceDetail } from '@/hooks/useSpaceDetail';
import { meetsRole } from '@/lib/space-roles';
import type { SpaceRole } from '@/types/spaces';

const ROLE_BADGE: Record<SpaceRole, 'purple' | 'info' | 'gray' | 'teal'> = {
  owner: 'purple',
  admin: 'info',
  member: 'teal',
  viewer: 'gray',
};

export function GeneralTab() {
  const navigate = useNavigate();
  const { spaceId, role: callerRole, isPersonal } = useSpaceContext();
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const { data: detail } = useSpaceDetail(spaceId);
  const space = detail?.space;

  const updateSpace = useUpdateSpace(spaceId);
  const unarchiveSpace = useUnarchiveSpace(spaceId);
  const removeMember = useRemoveMember(spaceId);

  // Local mirror for description so the textarea stays controlled even while
  // the mutation is in flight; reset whenever the upstream description shifts.
  const [description, setDescription] = useState(space?.description ?? '');
  useEffect(() => {
    setDescription(space?.description ?? '');
  }, [space?.description]);

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [unarchiveOpen, setUnarchiveOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const isAdminPlus = meetsRole(callerRole, 'admin');
  const isOwner = meetsRole(callerRole, 'owner');
  const isArchived = space?.status === 'archived';

  // Save name on blur from the inline editor.
  const handleNameSave = async (next: string) => {
    if (!next.trim() || !space || next.trim() === space.name) return;
    try {
      await updateSpace.mutateAsync({ name: next.trim() });
      success('Space renamed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to rename');
    }
  };

  const handleDescriptionSave = async () => {
    const trimmed = description.trim();
    if (trimmed === (space?.description ?? '').trim()) return;
    try {
      await updateSpace.mutateAsync({ description: trimmed });
      success('Description updated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update description');
    }
  };

  const handleArchive = async () => {
    try {
      await updateSpace.mutateAsync({ archived: true });
      success('Space archived');
      setArchiveOpen(false);
      navigate('/c', { replace: true });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to archive');
    }
  };

  const handleUnarchive = async () => {
    try {
      await unarchiveSpace.mutateAsync();
      success('Space restored');
      setUnarchiveOpen(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to unarchive');
    }
  };

  const handleLeave = async () => {
    if (!user?.userId) return;
    try {
      await removeMember.mutateAsync(user.userId);
      success('You left the space');
      setLeaveOpen(false);
      navigate('/c', { replace: true });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to leave');
    }
  };

  if (!space) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Details" description="The space's name and description are visible to every member." />
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-foreground">Name</p>
            <div className="mt-1">
              {isAdminPlus ? (
                <InlineEdit
                  value={space.name}
                  onSave={handleNameSave}
                  placeholder="Untitled space"
                  className="text-2xl font-bold text-foreground"
                  inputClassName="text-2xl font-bold"
                />
              ) : (
                <p className="text-2xl font-bold text-foreground">{space.name}</p>
              )}
            </div>
            {!isAdminPlus && (
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Lock className="h-3 w-3" /> Admins and the owner can rename.
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-foreground">Description</p>
            {isAdminPlus ? (
              <Textarea
                value={description}
                onChange={(e: { target: { value: string } }) => setDescription(e.target.value)}
                onBlur={handleDescriptionSave}
                placeholder="A short summary of what this space is for…"
                rows={3}
                className="mt-1"
              />
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                {space.description || <span className="italic">No description set.</span>}
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Metadata" />
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Space ID</dt>
            <dd className="mt-1 flex items-center gap-2">
              <span className="font-mono text-xs text-foreground">{space.spaceId}</span>
              <CopyButton value={space.spaceId} />
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Kind</dt>
            <dd className="mt-1">
              <Badge variant={space.kind === 'personal' ? 'gray' : 'info'}>
                {space.kind}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Your role</dt>
            <dd className="mt-1">
              {callerRole ? <Badge variant={ROLE_BADGE[callerRole]}>{callerRole}</Badge> : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Created</dt>
            <dd className="mt-1 text-sm text-foreground">
              {new Date(space.createdAt).toLocaleDateString()}
            </dd>
          </div>
        </dl>
      </Card>

      {/* Danger zone — hidden for Personal Space (immortal). Each row gates by role. */}
      {!isPersonal && (
        <Card className="border-red-200 dark:border-red-800">
          <CardHeader title="Danger zone" description="Irreversible and destructive actions." />
          <div className="space-y-3">
            {isOwner && !isArchived && (
              <ActionRow
                label="Archive this space"
                description="Archived spaces are hidden from members and viewers. You can restore later from the admin-only archived view."
                button={
                  <Button variant="danger" onClick={() => setArchiveOpen(true)}>
                    <Archive className="mr-2 h-4 w-4" />
                    Archive
                  </Button>
                }
              />
            )}
            {isAdminPlus && isArchived && (
              <ActionRow
                label="Restore this space"
                description="Brings the space back to active status."
                button={
                  <Button variant="secondary" onClick={() => setUnarchiveOpen(true)}>
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                    Unarchive
                  </Button>
                }
              />
            )}
            {!isOwner && (
              <ActionRow
                label="Leave this space"
                description="You'll lose access immediately. An admin would have to re-add you."
                button={
                  <Button variant="danger" onClick={() => setLeaveOpen(true)}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Leave Space
                  </Button>
                }
              />
            )}
          </div>
        </Card>
      )}

      <ConfirmModal
        isOpen={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        onConfirm={handleArchive}
        title="Archive this space?"
        message={`Members will lose access to ${space.name} until you (or another admin) restore it.`}
        confirmText="Archive"
        isLoading={updateSpace.isPending}
      />

      <ConfirmModal
        isOpen={unarchiveOpen}
        onClose={() => setUnarchiveOpen(false)}
        onConfirm={handleUnarchive}
        title="Restore this space?"
        message={`${space.name} will become active again.`}
        confirmText="Restore"
        variant="default"
        isLoading={unarchiveSpace.isPending}
      />

      <ConfirmModal
        isOpen={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        onConfirm={handleLeave}
        title="Leave this space?"
        message={`You'll lose access to ${space.name} immediately.`}
        confirmText="Leave"
        isLoading={removeMember.isPending}
      />
    </div>
  );
}

function ActionRow({
  label,
  description,
  button,
}: {
  label: string;
  description: string;
  button: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="shrink-0">{button}</div>
    </div>
  );
}
